// src/components/admin/SimpleAdminItemForm.jsx
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";

// ✅ pour les schemas "flatten" : ex "profilePhoto.url" => "profilePhoto"
const getBaseField = (field) => String(field || "").split(".")[0];

// ✅ helper: detect image fields
const isImageField = (field) => {
  if (!field) return false;
  const f = field.toLowerCase();
  return (
    f === "image" ||
    f === "profileimage" ||
    f === "profilephoto" ||
    f === "avatar" ||
    f.includes("image") ||
    f.includes("photo") ||
    f.includes("avatar")
  );
};

// ✅ helpers: lecture/écriture de champs imbriqués ("inclus.transport")
const getNested = (obj, path) =>
  String(path || "")
    .split(".")
    .reduce((o, k) => (o === undefined || o === null ? undefined : o[k]), obj);

const setNested = (obj, path, val) => {
  const keys = String(path || "").split(".");
  const next = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  let cur = next;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = cur[k] && typeof cur[k] === "object" ? (Array.isArray(cur[k]) ? [...cur[k]] : { ...cur[k] }) : {};
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = val;
  return next;
};

// ✅ Éditeur de "tags" (tableaux de chaînes : highlights, galerie, etc.)
const TagsEditor = ({ icon, label, hint, value, onChange, placeholder, preview = false, required = false }) => {
  const [draft, setDraft] = useState("");
  const tags = Array.isArray(value)
    ? value
    : typeof value === "string" && value
    ? value.split(",").map((s) => s.trim()).filter(Boolean)
    : [];

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (!tags.includes(v)) onChange([...tags, v]);
    setDraft("");
  };
  const remove = (idx) => onChange(tags.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
      <label className="flex items-center gap-3 text-sm font-bold text-slate-800">
        <span className="text-xl">{icon}</span>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
        <span className="ml-auto text-xs font-medium text-slate-400">{tags.length} élément(s)</span>
      </label>

      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          className="flex-1 px-4 py-3 border-2 border-slate-300 rounded-xl focus:border-amber-400 focus:ring-4 focus:ring-amber-200/40 transition-all duration-300 bg-slate-50 focus:bg-white"
        />
        <button
          type="button"
          onClick={add}
          className="px-4 py-3 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-600 transition-all flex items-center gap-1 shadow-sm"
        >
          ➕ Ajouter
        </button>
      </div>

      {tags.length > 0 ? (
        preview ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {tags.map((t, idx) => (
              <div key={`${t}-${idx}`} className="relative group aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                <img
                  src={t}
                  alt={`Galerie ${idx + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.parentNode.classList.add("flex", "items-center", "justify-center");
                    const span = document.createElement("span");
                    span.textContent = "🔗";
                    span.className = "text-2xl";
                    e.currentTarget.parentNode.appendChild(span);
                  }}
                />
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all text-xs"
                >
                  ❌
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-xl">
            {tags.map((t, idx) => (
              <span
                key={`${t}-${idx}`}
                className="inline-flex items-center gap-1 px-3 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium border border-amber-200"
              >
                {t}
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="ml-1 text-amber-600 hover:text-amber-800 transition-colors hover:bg-amber-200 rounded-full w-4 h-4 flex items-center justify-center"
                  title="Supprimer"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )
      ) : (
        <div className="text-center py-5 bg-slate-50 rounded-xl border-2 border-dashed border-slate-300">
          <p className="text-sm text-slate-400">Aucun élément. Tapez puis « Ajouter » (ou Entrée).</p>
        </div>
      )}

      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
};

// ✅ helper: parse status
const normalizeStatus = (v) => {
  if (v === true || v === "true") return "active";
  if (v === false || v === "false") return "inactive";
  const s = String(v || "").toLowerCase();
  if (["active", "actif", "enabled", "enable", "shown", "show", "1"].includes(s)) return "active";
  if (["inactive", "inactif", "disabled", "disable", "hidden", "hide", "0"].includes(s)) return "inactive";
  return v || "inactive";
};

const SimpleAdminItemForm = ({ modelName, schema, initialData, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedCities, setSelectedCities] = useState([]);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ IMPORTANT: gérer plusieurs fichiers images (image + profileImage + profilePhoto etc.)
  const [fileMap, setFileMap] = useState({}); // { [fieldName]: File }

  // ✅ Circuit: hôtels par ville
  const [hotelsByCity, setHotelsByCity] = useState({});
  // { [city]: { loading:boolean, error:string|null, hotels:Array } }

  const [selectedHotelByCity, setSelectedHotelByCity] = useState({});
  // { [city]: { hotelId, nom, image } | null }

  const API_BASE =
    (process.env.REACT_APP_API_BASE_URL ||
      process.env.VITE_API_BASE_URL ||
      "https://nouzhaback-theta.vercel.app").replace(/\/$/, "");

  const normalizedModel = useMemo(() => String(modelName || "").toLowerCase(), [modelName]);

  const isCommentModel = useMemo(() => {
    return [
      "commentaires",
      "commentaire",
      "temoignages",
      "temoignage",
      "testimonials",
      "testimonial",
      "reviews_home",
    ].includes(normalizedModel);
  }, [normalizedModel]);

  const isCircuitModel = useMemo(() => {
    return ["circuits", "circuit"].includes(normalizedModel);
  }, [normalizedModel]);

  // Liste des villes du Maroc
  const moroccanCities = [
    "Casablanca",
    "Rabat",
    "Fès",
    "Marrakech",
    "Merzouga",
    "Agadir",
    "Tanger",
    "Meknès",
    "Oujda",
    "Kenitra",
    "Tétouan",
    "Safi",
    "Mohammedia",
    "Khouribga",
    "El Jadida",
    "Béni Mellal",
    "Errachidia",
    "Taza",
    "Essaouira",
    "Ksar el-Kebir",
    "Settat",
    "Larache",
    "Khemisset",
    "Guelmim",
    "Berrechid",
    "Wazzane",
    "Ouarzazate",
    "Tiznit",
    "Taourirt",
    "Nador",
    "Berkane",
    "Sidi Kacem",
    "Khénifra",
    "Sidi Slimane",
    "Al Hoceima",
    "Taroudant",
    "Oulad Teima",
    "Youssoufia",
    "Fquih Ben Salah",
    "Ouezzane",
    "Guercif",
    "Ifrane",
    "Azrou",
    "Midelt",
    "Zagora",
    "Tinghir",
    "Chefchaouen",
    "Autre",
  ].sort();

  // ✅ helper: schema type normalize
  const getSchemaType = React.useCallback((field) => {
    const t = schema?.[field]?.type;
    if (!t) return null;
    if (typeof t === "string") return t.toLowerCase();
    if (t === Boolean) return "boolean";
    if (t === Number) return "number";
    if (t === Date) return "date";
    if (t === String) return "string";
    return String(t).toLowerCase();
  }, [schema]);

  // ✅ Circuit: fetch hôtels par ville (GET /api/hotels/ville/:ville)
  const fetchHotelsForCity = async (city) => {
    if (!city || city === "Autre") return;
    if (hotelsByCity?.[city]?.hotels?.length) return;

    setHotelsByCity((prev) => ({
      ...prev,
      [city]: { loading: true, error: null, hotels: prev?.[city]?.hotels || [] },
    }));

    try {
      const url = `${API_BASE}/api/hotels/ville/${encodeURIComponent(city)}`;
      const res = await axios.get(url);

      // compat: API renvoie soit [] soit { data: [] }
      const hotels = Array.isArray(res.data) ? res.data : res.data?.data || [];

      setHotelsByCity((prev) => ({
        ...prev,
        [city]: { loading: false, error: null, hotels },
      }));
    } catch (e) {
      setHotelsByCity((prev) => ({
        ...prev,
        [city]: {
          loading: false,
          error: e?.response?.data?.message || e?.message || "Erreur chargement hôtels",
          hotels: [],
        },
      }));
    }
  };

  // ✅ quand selectedCities change (circuit), charger hôtels
  useEffect(() => {
    if (!isCircuitModel) return;
    if (!Array.isArray(selectedCities)) return;

    selectedCities.forEach((c) => fetchHotelsForCity(c));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCircuitModel, selectedCities]);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);

      // ✅ previews pour image fields depuis l’URL existante
      const firstImgField = Object.keys(schema || {}).find((f) => isImageField(f));
      if (firstImgField && initialData[firstImgField]) setImagePreview(initialData[firstImgField]);

      // villes circuits
      if (initialData.villes && Array.isArray(initialData.villes)) {
        setSelectedCities(initialData.villes);
      } else if (initialData.villes && typeof initialData.villes === "string") {
        setSelectedCities(initialData.villes.split(",").map((v) => v.trim()).filter((v) => v));
      } else {
        setSelectedCities([]);
      }

      // ✅ Circuit: pré-remplir hôtelsByVille si existant
      if (isCircuitModel) {
        const hv = initialData?.hotelsByVille;
        if (hv && typeof hv === "object") {
          setSelectedHotelByCity(hv);
        } else if (hv && typeof hv === "string") {
          try {
            setSelectedHotelByCity(JSON.parse(hv));
          } catch {
            setSelectedHotelByCity({});
          }
        } else {
          setSelectedHotelByCity({});
        }
      } else {
        setSelectedHotelByCity({});
      }

      setFileMap({});
    } else {
      let emptyData = {};
      Object.keys(schema || {}).forEach((field) => {
        if (field === "demande_facture") emptyData[field] = false;
        else if (field === "historique_commande") emptyData[field] = [];
        else if (["villes", "galerie", "highlights", "services", "equipements", "points_forts", "inclusions", "exclusions"].includes(field)) emptyData[field] = [];
        else if (field === "etapes") emptyData[field] = [];
        else if (field === "role") emptyData[field] = "user";
        else if (field === "disponible") emptyData[field] = true;
        else if (field.includes(".")) emptyData = setNested(emptyData, field, "");
        else if (getSchemaType(field) === "number") emptyData[field] = 0;
        else if (field === "status" && isCommentModel) emptyData[field] = "inactive";
        else emptyData[field] = "";
      });

      setFormData(emptyData);
      setSelectedCities([]);
      setImagePreview(null);
      setFileMap({});
      setHotelsByCity({});
      setSelectedHotelByCity({});
    }
  }, [initialData, schema, isCommentModel, isCircuitModel, getSchemaType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const cleanedData = { ...formData };

      // ✅ Status commentaires : normaliser active/inactive
      if (isCommentModel && cleanedData.status !== undefined) {
        cleanedData.status = normalizeStatus(cleanedData.status);
      }

      // Nettoyer chaînes vides
      Object.keys(cleanedData).forEach((key) => {
        if (cleanedData[key] === "" && key !== "mdp" && key !== "username" && key !== "email") {
          delete cleanedData[key];
        }
      });

      // bool demande_facture
      if (Object.prototype.hasOwnProperty.call(cleanedData, "demande_facture")) {
        cleanedData.demande_facture = Boolean(cleanedData.demande_facture);
      }

      // bool voitures
      const booleanFields = ["climatisation", "disponible", "assurance_incluse", "kilometrage_illimite"];
      booleanFields.forEach((field) => {
        if (Object.prototype.hasOwnProperty.call(cleanedData, field)) {
          if (cleanedData[field] === "oui" || cleanedData[field] === true) cleanedData[field] = true;
          else if (cleanedData[field] === "non" || cleanedData[field] === false) cleanedData[field] = false;
        }
      });

      // villes array
      if (Object.prototype.hasOwnProperty.call(cleanedData, "villes")) {
        if (Array.isArray(selectedCities) && selectedCities.length > 0) cleanedData.villes = selectedCities;
        else if (!Array.isArray(cleanedData.villes)) cleanedData.villes = [];
      }

      // ✅ Build payload FormData (support multi-file)
      const payload = new FormData();

      // ✅ Circuits: NE PAS envoyer etapes (backend le génère)
      if (isCircuitModel) {
        delete cleanedData.etapes;
        delete cleanedData.hotelsByVille; // on le gère à part
      }

      // ✅ Append cleaned fields
      Object.entries(cleanedData).forEach(([key, val]) => {
        if (val === undefined || val === null) return;

        if (Array.isArray(val)) {
          payload.append(key, JSON.stringify(val));
          return;
        }
        if (typeof val === "object") {
          payload.append(key, JSON.stringify(val));
          return;
        }
        payload.append(key, String(val));
      });

      // ✅✅✅ FIX PRINCIPAL : hotelsByVille doit être EXACTEMENT comme Postman
      // { "Fès": { "hotelId": "..." }, ... }
      if (isCircuitModel) {
        const cleanedHotelsByVille = {};

        console.log("DEBUG: selectedHotelByCity before submit:", selectedHotelByCity);

        Object.entries(selectedHotelByCity || {}).forEach(([city, obj]) => {
          const ville = String(city || "").trim();
          const hotelId = obj?.hotelId || obj?._id || obj?.id || null;

          if (ville && ville !== "Autre" && hotelId) {
            cleanedHotelsByVille[ville] = { hotelId }; // ✅ uniquement hotelId (comme Postman)
          } else {
            console.warn(`DEBUG: Skipping city ${ville} because hotelId is missing or invalid`, obj);
          }
        });

        console.log("DEBUG: cleanedHotelsByVille to be sent:", cleanedHotelsByVille);

        // IMPORTANT: Stringify is crucial here as verified in Postman logic
        payload.append("hotelsByVille", JSON.stringify(cleanedHotelsByVille));
      }

      // ✅ append files
      Object.entries(fileMap).forEach(([field, file]) => {
        if (Array.isArray(file)) {
          file.forEach(f => payload.append(field, f));
        } else if (file) {
          payload.append(field, file);
        }
      });

      // ✅ log FormData (corrigé)
      console.log("=== FormData envoyé ===");
      for (const [k, v] of payload.entries()) {
        console.log(k, v);
      }

      await onSubmit(payload);
      onCancel();
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);
      let errorMessage = "Une erreur inattendue s'est produite.";

      if (error.response?.data?.message) errorMessage = error.response.data.message;
      else if (error.message) errorMessage = error.message;

      setErrorModal({ show: true, message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ upload image générique
  const handleImageUpload = (field, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const baseField = getBaseField(field);
    setFileMap((prev) => ({ ...prev, [baseField]: file }));

    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);

    setFormData((prev) => ({ ...prev, [field]: "" }));
  };

  // ✅ upload MULTIPLE images (album)
  const handleImagesUpload = (field, event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    // Use full field path for nested fields (e.g. roomTypes[0].images)
    const isNested = field.includes("[");
    const storageKey = isNested ? field : getBaseField(field);

    // Add to fileMap (array)
    setFileMap((prev) => {
      const existing = prev[storageKey] || [];
      const newFiles = Array.isArray(existing) ? [...existing, ...files] : [...files];
      return { ...prev, [storageKey]: newFiles };
    });
  };

  const removeNewImage = (field, fileIndex) => {
    const isNested = field.includes("[");
    const storageKey = isNested ? field : getBaseField(field);

    setFileMap(prev => {
      const files = prev[storageKey];
      if (!Array.isArray(files)) return prev;
      const newFiles = files.filter((_, i) => i !== fileIndex);
      return { ...prev, [storageKey]: newFiles };
    });
  };

  const removeExistingImage = (field, urlToRemove, isNested = false) => {
    setFormData(prev => {
      if (isNested) {
        // Deep update for nested paths like roomTypes[0].images
        const next = { ...prev };
        const path = field.split(/[.[\]]+/).filter(Boolean);
        let current = next;

        for (let i = 0; i < path.length - 1; i++) {
          const key = path[i];
          current[key] = Array.isArray(current[key]) ? [...current[key]] : { ...current[key] };
          current = current[key];
        }

        const lastKey = path[path.length - 1];
        if (Array.isArray(current[lastKey])) {
          current[lastKey] = current[lastKey].filter(url => url !== urlToRemove);
        }
        return next;
      }

      const current = prev[field] || [];
      if (!Array.isArray(current)) return prev;
      return { ...prev, [field]: current.filter(url => url !== urlToRemove) };
    });
  };

  const clearImageField = (field) => {
    const baseField = getBaseField(field);

    setFileMap((prev) => {
      const next = { ...prev };
      delete next[baseField];
      return next;
    });

    setImagePreview(null);
    setFormData((prev) => ({ ...prev, [field]: "" }));

    const input = document.getElementById(`${field.replace(/\./g, "_")}-upload`);
    if (input) input.value = "";
  };



  const getFieldIcon = (fieldKey) => {
    const iconMap = {
      nom: "🏨",
      ville: "📍",
      image: "📸",
      images: "📸",
      profileImage: "🧑‍💼",
      profilePhoto: "🧑‍💼",
      description: "📝",
      nb_etoiles: "⭐",
      note: "⭐",
      prix: "💰",
      date: "📅",
      participants: "👥",
      stars: "⭐",
      etoiles: "⭐",
      commentaire: "💬",
      comment: "💬",
      country: "🌍",
      ville_client: "🏙️",
      status: "✅",
      titre: "🏷️",
      duree: "⏱️",
      galerie: "🖼️",
      highlights: "✨",
      etapes: "🗺️",
      avis: "💬",
      "inclus.transport": "🚌",
      "inclus.hebergement": "🏨",
      "inclus.visite": "🧭",
      "inclus.activites": "🎯",
      services: "🛎️",
      equipements: "🧰",
      points_forts: "✨",
      inclusions: "✅",
      exclusions: "⛔",
      type: "🏷️",
      type_voiture: "🚗",
      transmission: "⚙️",
      carburant: "⛽",
    };
    return iconMap[fieldKey] || "📝";
  };

  const getFieldDisplayName = (fieldName) => {
    const translations = {
      nom: "Nom",
      prenom: "Prénom",
      ville: "Ville",
      villes: "Villes",
      image: "Image (Principale)",
      images: "Album Photos",
      description: "Description",
      nb_etoiles: "Nombre d'étoiles",
      note: "Note",
      prix: "Prix",
      date: "Date",
      participants: "Participants",
      username: "Nom d'utilisateur",
      email: "Adresse email",
      mdp: "Mot de passe",
      role: "Rôle",
      phone: "Téléphone",
      address: "Adresse",
      bio: "Biographie",
      demande_facture: "Demande de facture",
      historique_commande: "Historique des commandes",
      profileImage: "Image de profil",
      profilePhoto: "Photo de profil",
      ville_source: "Point de départ",
      ville_destination: "Point d'arrivée",
      point_depart: "Adresse de départ",
      point_arrivee: "Adresse d'arrivée",
      type_voiture: "Type de voiture",
      marque: "Marque",
      modele: "Modèle",
      nb_places: "Nombre de places",
      transmission: "Transmission",
      carburant: "Carburant",
      climatisation: "Climatisation",
      prix_jour: "Prix par jour",
      disponible: "Disponible",
      assurance_incluse: "Assurance incluse",
      kilometrage_illimite: "Kilométrage illimité",
      date_debut: "Date de début",
      date_fin: "Date de fin",
      heure_depart: "Heure de départ",
      heure_arrivee: "Heure d'arrivée",
      stars: "Étoiles",
      etoiles: "Étoiles",
      comment: "Commentaire",
      commentaire: "Commentaire",
      country: "Pays",
      city: "Ville",
      status: "Statut (Home)",
      roomTypes: "Types de Chambres",
      titre: "Titre",
      duree: "Durée",
      galerie: "Galerie photos",
      highlights: "Points forts",
      etapes: "Étapes du circuit",
      avis: "Nombre d'avis",
      inclus: "Ce qui est inclus",
      "inclus.transport": "Inclus · Transport",
      "inclus.hebergement": "Inclus · Hébergement",
      "inclus.visite": "Inclus · Visites",
      "inclus.activites": "Inclus · Activités",
      services: "Services",
      equipements: "Équipements",
      points_forts: "Points forts",
      inclusions: "Ce qui est inclus",
      exclusions: "Non inclus",
      type: "Type",
    };
    return translations[fieldName] || fieldName;
  };

  // ✅ Placeholders d'exemple pour guider l'admin
  const getFieldPlaceholder = (field) => {
    const map = {
      titre: "Ex: Nord & Capital : Tanger → Rabat",
      duree: "Ex: 5 jours / 4 nuits",
      participants: "Ex: 2-10",
      prix: "Ex: 4400",
      "inclus.transport": "Ex: Transport en bus climatisé entre les villes",
      "inclus.hebergement": "Ex: Hôtels 4★ en demi-pension",
      "inclus.visite": "Ex: Visites guidées des médinas et monuments",
      "inclus.activites": "Ex: Excursion désert + balade à dos de chameau",
    };
    return map[field] || `Saisir ${getFieldDisplayName(field).toLowerCase()}...`;
  };

  // ✅ Petites aides sous certains champs
  const getFieldHint = (field) => {
    const map = {
      titre: "Le titre affiché sur la carte du circuit.",
      duree: "Format libre, ex : « 5 jours / 4 nuits ».",
      participants: "Fourchette du nombre de voyageurs, ex : « 2-10 ».",
      note: "Note moyenne sur 5 (laisser à 0 si aucun avis).",
      avis: "Nombre total d'avis reçus (laisser à 0 au départ).",
      "inclus.transport": "Décrivez le transport compris dans le prix.",
      "inclus.hebergement": "Décrivez l'hébergement (catégorie, pension).",
      "inclus.visite": "Décrivez les visites guidées incluses.",
      "inclus.activites": "Décrivez les activités / excursions incluses.",
    };
    return map[field] || null;
  };

  const isFieldCityRelated = (fieldName) => {
    if (fieldName === "villes") return false;

    if (isCommentModel) {
      const f = fieldName.toLowerCase();
      if (f === "ville" || f === "city" || f.includes("ville") || f.includes("city")) {
        return false;
      }
    }

    const cityFields = [
      "ville",
      "ville_source",
      "ville_destination",
      "ville_depart",
      "ville_arrivee",
      "location",
      "localisation",
      "city",
    ];

    const addressFields = ["point_depart", "point_arrivee"];
    if (addressFields.includes(fieldName.toLowerCase())) return false;

    return (
      cityFields.includes(fieldName.toLowerCase()) ||
      fieldName.toLowerCase().includes("ville") ||
      fieldName.toLowerCase().includes("city") ||
      fieldName.toLowerCase().includes("location")
    );
  };

  const isDateTimeField = (field) => {
    return field.toLowerCase().includes("date") || getSchemaType(field) === "date";
  };

  // ✅ écriture (gère les champs imbriqués type "inclus.transport")
  const updateField = (field, val) =>
    setFormData((prev) => (field.includes(".") ? setNested(prev, field, val) : { ...prev, [field]: val }));

  // ✅ Champs enum → liste déroulante (au lieu d'un input texte libre)
  const getEnumOptions = (field) => {
    const base = {
      type_voiture: ["citadine", "berline", "suv", "break", "cabriolet", "monospace", "utilitaire"],
      transmission: ["manuelle", "automatique"],
      carburant: ["essence", "diesel", "hybride", "electrique"],
    };
    if (base[field]) return base[field];
    if (field === "type") {
      if (normalizedModel.includes("visite")) return ["culturel", "historique", "nature", "gastronomique", "autre"];
      if (normalizedModel.includes("transport")) return ["navette_aeroport", "mise_a_disposition", "trajet"];
    }
    return null;
  };

  const prettyOption = (v) =>
    String(v || "")
      .replace(/_/g, " ")
      .replace(/^\w/, (c) => c.toUpperCase());

  const renderEnumSelect = (field, options) => {
    const value = formData[field] ?? "";
    const icon = getFieldIcon(field);
    return (
      <div key={field} className="space-y-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <label className="flex items-center gap-3 text-sm font-bold text-slate-800">
          <span className="text-xl">{icon}</span>
          {getFieldDisplayName(field)}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <div className="relative">
          <select
            value={value}
            onChange={(e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }))}
            className="w-full px-4 py-3 pr-12 border-2 border-slate-300 rounded-xl focus:border-orange-400 focus:ring-4 focus:ring-orange-200/40 transition-all duration-300 bg-white appearance-none cursor-pointer"
            required
          >
            <option value="">Sélectionner {getFieldDisplayName(field).toLowerCase()}...</option>
            {options.map((opt) => (
              <option key={opt} value={opt}>
                {prettyOption(opt)}
              </option>
            ))}
          </select>
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>
    );
  };

  // ✅ Circuit: étapes auto-générées (lecture seule)
  const renderEtapesInfo = (field) => {
    const etapes = Array.isArray(formData[field]) ? formData[field] : [];
    return (
      <div key={field} className="space-y-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <label className="flex items-center gap-3 text-sm font-bold text-slate-800">
          <span className="text-xl">🗺️</span>
          {getFieldDisplayName(field)}
          <span className="ml-auto text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 px-2 py-1 rounded-full">Automatique</span>
        </label>
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
          {etapes.length > 0 ? (
            <ul className="space-y-1.5">
              {etapes.map((e, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                  <span className="w-6 h-6 flex-none rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">{i + 1}</span>
                  <span className="font-medium">📍 {e?.ville || "Ville"}</span>
                  <span className="text-slate-400">→ hôtel associé ✅</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-400">Les étapes seront générées automatiquement à partir des villes et des hôtels choisis ci-dessous.</p>
          )}
        </div>
        <p className="text-xs text-slate-500">
          ℹ️ Ce champ est <b>calculé automatiquement</b> : choisissez les villes puis un hôtel par ville dans la section « Hôtels associés ».
        </p>
      </div>
    );
  };

  const renderStatusField = (field) => {
    const icon = getFieldIcon(field);
    const value = normalizeStatus(formData[field]);

    return (
      <div key={field} className="space-y-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <label className="flex items-center gap-3 text-sm font-bold text-slate-800">
          <span className="text-xl">{icon}</span>
          {getFieldDisplayName(field)}
          <span className="text-red-500 ml-1">*</span>
        </label>

        <div className="relative">
          <select
            value={value}
            onChange={(e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }))}
            className="w-full px-4 py-3 pr-12 border-2 border-slate-300 rounded-xl focus:border-orange-400 focus:ring-4 focus:ring-orange-200/40 transition-all duration-300 bg-white appearance-none cursor-pointer"
            required
          >
            <option value="active">✅ Active (affiché sur Home)</option>
            <option value="inactive">🚫 Non-active (masqué)</option>
          </select>

          <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        <p className="text-xs text-slate-500">
          Astuce: mets seulement 3 commentaires en <b>Active</b> pour qu’ils s’affichent sur la Home.
        </p>
      </div>
    );
  };

  const renderStarsField = (field) => {
    const icon = getFieldIcon(field);
    const raw = formData[field];
    const value = raw === undefined || raw === null || raw === "" ? 5 : Number(raw) || 5;

    return (
      <div key={field} className="space-y-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <label className="flex items-center gap-3 text-sm font-bold text-slate-800">
          <span className="text-xl">{icon}</span>
          {getFieldDisplayName(field)}
          <span className="text-red-500 ml-1">*</span>
        </label>

        <div className="flex items-center gap-4">
          <input
            type="range"
            min={1}
            max={5}
            value={value}
            onChange={(e) => setFormData((prev) => ({ ...prev, [field]: Number(e.target.value) }))}
            className="w-full"
          />
          <div className="min-w-[64px] text-center font-bold text-orange-600 text-lg">{value}⭐</div>
        </div>

        <div className="text-xs text-slate-500">Choisis entre 1 et 5 étoiles.</div>
      </div>
    );
  };

  const renderImagesField = (field, isNested = false) => {
    const icon = "📸";

    // Resolve existing images from formData (handle nested paths)
    const getNestedValue = (obj, path) => {
      return path.split(/[.[\]]+/).filter(Boolean).reduce((val, key) => (val && val[key] !== undefined ? val[key] : undefined), obj);
    };

    const existingImages = isNested ? getNestedValue(formData, field) || [] : formData[field] || [];
    const newFiles = fileMap[field] || [];
    const inputId = `${field.replace(/[.[\]]+/g, "_")}-upload-multi`;

    return (
      <div key={field} className={`space-y-3 ${isNested ? "" : "bg-white p-4 rounded-xl border border-slate-200 shadow-sm"}`}>
        {!isNested && (
          <label className="flex items-center gap-3 text-sm font-bold text-slate-800">
            <span className="text-xl">{icon}</span>
            {getFieldDisplayName(field)} (Album)
          </label>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Existing Images */}
          {Array.isArray(existingImages) && existingImages.map((url, idx) => (
            <div key={`existing-${idx}`} className="relative group aspect-square rounded-xl overflow-hidden border border-slate-200">
              <img src={url} alt={`Aperçu ${idx}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeExistingImage(field, url, isNested)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all"
              >
                ❌
              </button>
              <div className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[10px] p-1 text-center font-bold">En ligne</div>
            </div>
          ))}

          {/* New Files */}
          {Array.isArray(newFiles) && newFiles.map((file, idx) => (
            <div key={`new-${idx}`} className="relative group aspect-square rounded-xl overflow-hidden border border-green-200 ring-2 ring-green-100">
              <img src={URL.createObjectURL(file)} alt="Preview" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeNewImage(field, idx)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all"
              >
                ❌
              </button>
              <div className="absolute bottom-0 inset-x-0 bg-green-500/80 text-white text-[10px] p-1 text-center font-bold">Nouveau</div>
            </div>
          ))}

          {/* Add Button */}
          <div className="aspect-square">
            <input
              type="file"
              id={inputId}
              multiple
              accept="image/*"
              onChange={(e) => handleImagesUpload(field, e)}
              className="hidden"
            />
            <label htmlFor={inputId} className="flex flex-col items-center justify-center w-full h-full border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:bg-slate-50 hover:border-amber-400 transition-all">
              <span className="text-2xl">➕</span>
              <span className="text-[10px] text-slate-500 mt-1 font-bold">Ajouter</span>
            </label>
          </div>
        </div>
        {!isNested && <p className="text-xs text-slate-500">Vous pouvez ajouter plusieurs photos pour l'album. "Image" (singulier) reste l'image principale.</p>}
      </div>
    );
  };

  const renderRoomTypesField = (field) => {
    const roomTypes = formData[field] || [];

    const addRoomType = () => {
      setFormData((prev) => ({
        ...prev,
        [field]: [...(prev[field] || []), { name: "", price: 0, images: [] }],
      }));
    };

    const removeRoomType = (index) => {
      const newRooms = roomTypes.filter((_, i) => i !== index);
      setFormData((prev) => ({ ...prev, [field]: newRooms }));

      // Clean up fileMap
      setFileMap((prev) => {
        const next = { ...prev };
        const prefix = `${field}[${index}]`;
        Object.keys(next).forEach((key) => {
          if (key.startsWith(prefix)) delete next[key];
        });
        // Note: Shifting indices in fileMap is complex if we handle reordering, 
        // but for simple removal it might leave orphaned files if we're not careful.
        // For now, simple removal.
        return next;
      });
    };

    const updateRoom = (index, subField, value) => {
      const newRooms = [...roomTypes];
      newRooms[index] = { ...newRooms[index], [subField]: value };
      setFormData((prev) => ({ ...prev, [field]: newRooms }));
    };

    return (
      <div key={field} className="col-span-full space-y-4 bg-slate-50 p-6 rounded-2xl border-2 border-dashed border-slate-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <span>🛏️</span> {getFieldDisplayName(field)}
          </h3>
          <button
            type="button"
            onClick={addRoomType}
            className="px-4 py-2 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-600 transition-all flex items-center gap-2 shadow-sm"
          >
            <span>➕</span> Ajouter une chambre
          </button>
        </div>

        {roomTypes.length === 0 ? (
          <div className="text-center py-8 text-slate-400 italic bg-white rounded-xl border border-slate-100">
            Aucun type de chambre défini.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {roomTypes.map((room, idx) => (
              <div key={idx} className="relative bg-white p-5 rounded-2xl border border-slate-200 shadow-md space-y-4">
                <button
                  type="button"
                  onClick={() => removeRoomType(idx)}
                  className="absolute -top-3 -right-3 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-600 transition-all z-10 font-bold"
                >
                  ×
                </button>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Type / Nom</label>
                    <input
                      type="text"
                      value={room.name || ""}
                      onChange={(e) => updateRoom(idx, "name", e.target.value)}
                      placeholder="Ex: Chambre Double Deluxe"
                      className="w-full px-4 py-3 border-2 border-slate-100 rounded-xl focus:border-amber-400 outline-none transition-all font-medium bg-slate-50 focus:bg-white"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Prix (MAD / Nuit)</label>
                    <input
                      type="number"
                      value={room.price || 0}
                      onChange={(e) => updateRoom(idx, "price", Number(e.target.value))}
                      placeholder="0"
                      className="w-full px-4 py-3 border-2 border-slate-100 rounded-xl focus:border-amber-400 outline-none transition-all font-bold text-amber-600 bg-slate-50 focus:bg-white"
                    />
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-50">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 block">Photos de cette chambre</label>
                  {renderImagesField(`${field}[${idx}].images`, true)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderImageField = (field) => {
    const icon = getFieldIcon(field);
    const currentValue = formData[field];
    const baseField = getBaseField(field);

    const hasExistingUrl = Boolean(currentValue && typeof currentValue === "string" && currentValue.startsWith("http"));
    const inputId = `${field.replace(/\./g, "_")}-upload`;

    // Check if it's the specific "images" field
    if (field === "images") return renderImagesField(field);

    return (
      <div key={field} className="space-y-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <label className="flex items-center gap-3 text-sm font-bold text-slate-800">
          <span className="text-xl">{icon}</span>
          {getFieldDisplayName(field)}
        </label>

        <div className="relative">
          <input type="file" id={inputId} accept="image/*" onChange={(e) => handleImageUpload(field, e)} className="hidden" />
          <label
            htmlFor={inputId}
            className="group relative block w-full p-8 border-2 border-dashed border-amber-300 rounded-xl cursor-pointer bg-gradient-to-br from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 transition-all duration-300 hover:border-amber-400"
          >
            <div className="text-center">
              <div className="text-4xl mb-2">📸</div>
              <p className="text-lg font-medium text-slate-700 mb-1">
                {fileMap[baseField] || hasExistingUrl || imagePreview ? "Changer l'image" : "Sélectionner une image"}
              </p>
              <p className="text-sm text-slate-500">PNG, JPG, GIF jusqu'à 5MB</p>
            </div>
          </label>
        </div>

        {(fileMap[baseField] || imagePreview || hasExistingUrl) && (
          <div className="relative group">
            <div className="relative overflow-hidden rounded-xl bg-white shadow-lg">
              <img
                src={fileMap[baseField] ? URL.createObjectURL(fileMap[baseField]) : imagePreview ? imagePreview : currentValue}
                alt="Aperçu"
                className="w-full h-48 object-cover"
              />
              <button
                type="button"
                onClick={() => clearImageField(field)}
                className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-red-600"
              >
                ❌
              </button>
            </div>
            <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
              <span>✅</span>
              <span>Image prête</span>
            </div>
          </div>
        )}

        {isCommentModel && (field.toLowerCase().includes("profile") || field.toLowerCase().includes("photo")) && (
          <p className="text-xs text-slate-500">Photo de profil du client (sera uploadée sur Cloudinary).</p>
        )}
      </div>
    );
  };

  // ✅ Circuit UI: choisir un hôtel par ville
  const renderHotelsByCitySelector = () => {
    if (!isCircuitModel) return null;
    if (!selectedCities?.length) return null;

    const pickHotel = (city, hotel) => {
      setSelectedHotelByCity((prev) => ({
        ...prev,
        [city]: hotel
          ? {
            hotelId: hotel._id || hotel.id, // Robust ID check
            nom: hotel.nom,
            image: hotel.image
          }
          : null,
      }));
    };

    const isPicked = (city, hotelId) => selectedHotelByCity?.[city]?.hotelId === hotelId;
    const pickedCount = Object.values(selectedHotelByCity || {}).filter(Boolean).length;

    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-800">🏨 Hôtels associés par ville</h3>
            <p className="text-sm text-slate-500">Pour chaque ville du circuit, choisis un hôtel à recommander.</p>
          </div>
          <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 px-3 py-2 rounded-xl">
            {pickedCount}/{selectedCities.length} sélectionné(s)
          </div>
        </div>

        <div className="space-y-4">
          {selectedCities.map((city) => {
            const cityState = hotelsByCity?.[city] || { loading: false, error: null, hotels: [] };
            const hotels = cityState.hotels || [];
            const selected = selectedHotelByCity?.[city] || null;

            return (
              <div key={city} className="grid grid-cols-1 lg:grid-cols-12 gap-4 p-4 rounded-2xl border border-slate-200 bg-slate-50">
                <div className="lg:col-span-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center">📍</div>
                    <div>
                      <div className="font-bold text-slate-800">{city}</div>
                      <div className="text-xs text-slate-500">Choisir un hôtel pour {city}</div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white p-3">
                    {selected ? (
                      <div className="flex items-center gap-3">
                        <img src={selected.image} alt={selected.nom} className="w-12 h-12 rounded-xl object-cover border" />
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-800 truncate">{selected.nom}</div>
                          <button type="button" onClick={() => pickHotel(city, null)} className="text-xs text-red-600 hover:text-red-700">
                            Retirer la sélection
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-slate-500">Aucun hôtel sélectionné pour cette ville.</div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => fetchHotelsForCity(city)}
                    className="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white hover:bg-slate-100 text-sm font-medium"
                  >
                    🔄 Recharger les hôtels
                  </button>
                </div>

                <div className="lg:col-span-8">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold text-slate-700">Hôtels disponibles</div>
                    {cityState.loading && <div className="text-xs text-slate-500">Chargement...</div>}
                  </div>

                  {cityState.error ? (
                    <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{cityState.error}</div>
                  ) : cityState.loading ? (
                    <div className="p-4 rounded-xl bg-white border border-slate-200 text-sm text-slate-500">
                      Récupération des hôtels de {city}...
                    </div>
                  ) : hotels.length === 0 ? (
                    <div className="p-4 rounded-xl bg-white border border-slate-200 text-sm text-slate-500">
                      Aucun hôtel trouvé pour {city}.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                      {hotels.map((h) => (
                        <button
                          key={h._id}
                          type="button"
                          onClick={() => pickHotel(city, h)}
                          className={`text-left rounded-2xl border p-3 bg-white hover:shadow-md transition-all ${isPicked(city, h._id) ? "border-amber-400 ring-4 ring-amber-200/40" : "border-slate-200"
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <img src={h.image} alt={h.nom} className="w-14 h-14 rounded-2xl object-cover border" />
                            <div className="min-w-0">
                              <div className="font-semibold text-slate-800 truncate">{h.nom}</div>
                              <div className="text-xs text-slate-500 truncate">{h.ville || city}</div>
                              {isPicked(city, h._id) && <div className="text-xs text-amber-700 mt-1 font-medium">✅ Sélectionné</div>}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-xs text-slate-500">
          ℹ️ Cette association sera envoyée au backend comme un mapping <b>ville → hotelId</b> (comme Postman).
        </div>
      </div>
    );
  };

  const renderField = (field) => {
    const value = getNested(formData, field) ?? "";
    const icon = getFieldIcon(field);
    const schemaType = getSchemaType(field);

    if (["_id", "createdAt", "updatedAt", "__v"].includes(field)) return null;

    if (isCommentModel && field.toLowerCase() === "status") return renderStatusField(field);
    if (isCommentModel && ["stars", "etoiles", "nb_etoiles"].includes(field.toLowerCase())) return renderStarsField(field);
    if (field === "roomTypes") return renderRoomTypesField(field);
    if (field === "etapes") return renderEtapesInfo(field);
    if (isImageField(field)) return renderImageField(field);

    // ✅ Galerie de photos (tableau d'URLs) → éditeur avec miniatures
    if (field === "galerie") {
      return (
        <TagsEditor
          key={field}
          icon="🖼️"
          label={getFieldDisplayName(field)}
          value={value}
          onChange={(v) => updateField(field, v)}
          placeholder="Coller une URL d'image puis Entrée…"
          hint="Ajoutez les URLs des photos de la galerie. « Image » (au-dessus) reste la photo principale."
          preview
        />
      );
    }

    // ✅ Champs « tableau de chaînes » (highlights, et génériques) → éditeur de tags
    const isStringArray =
      field !== "villes" &&
      Array.isArray(value) &&
      value.every((x) => typeof x === "string");
    if (field === "highlights" || (isStringArray && field !== "images")) {
      const tagHints = {
        highlights: "Les temps forts mis en avant sur la fiche.",
        points_forts: "Les atouts mis en avant sur la fiche.",
        services: "Ex: Wifi, Piscine, Petit-déjeuner, Parking…",
        equipements: "Ex: GPS, Bluetooth, Climatisation, Sièges bébé…",
        inclusions: "Ce qui est compris dans le prix.",
        exclusions: "Ce qui n'est PAS compris dans le prix.",
      };
      return (
        <TagsEditor
          key={field}
          icon={icon}
          label={getFieldDisplayName(field)}
          value={value}
          onChange={(v) => updateField(field, v)}
          placeholder={`Ajouter ${getFieldDisplayName(field).toLowerCase()}…`}
          hint={tagHints[field] || null}
        />
      );
    }

    if (field === "mdp" || field === "password") {
      if (initialData) return null;
      return (
        <div key={field} className="space-y-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <label className="flex items-center gap-3 text-sm font-bold text-slate-800">
            <span className="text-xl">🔒</span>
            {getFieldDisplayName(field)}
            <span className="text-red-500 ml-1">*</span>
          </label>

          <input
            type="password"
            value={value}
            onChange={(e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }))}
            placeholder="Saisir le mot de passe..."
            required
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:border-amber-400 focus:ring-4 focus:ring-amber-200/40 transition-all duration-300 bg-slate-50 focus:bg-white"
          />

          <p className="text-xs text-slate-500">Le mot de passe doit contenir au moins 6 caractères</p>
        </div>
      );
    }

    if (field === "point_depart" || field === "point_arrivee") {
      return (
        <div key={field} className="space-y-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <label className="flex items-center gap-3 text-sm font-bold text-slate-800">
            <span className="text-xl">📍</span>
            {getFieldDisplayName(field)}
            <span className="text-red-500 ml-1">*</span>
          </label>

          <input
            type="text"
            value={value}
            onChange={(e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }))}
            placeholder={`Saisir ${getFieldDisplayName(field).toLowerCase()}... (ex: Aéroport Mohammed V, Terminal 1)`}
            required
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:border-orange-400 focus:ring-4 focus:ring-orange-200/40 transition-all duration-300"
          />

          <p className="text-xs text-slate-500">Saisir l'adresse complète (aéroport, hôtel, gare, etc.)</p>
        </div>
      );
    }

    const enumOptions = getEnumOptions(field);
    if (enumOptions) return renderEnumSelect(field, enumOptions);

    if (isFieldCityRelated(field)) {
      return (
        <div key={field} className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <span className="text-lg">📍</span>
            {getFieldDisplayName(field)}
            <span className="text-red-500">*</span>
          </label>

          <div className="relative">
            <select
              value={value}
              onChange={(e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }))}
              className="w-full px-4 py-3 pr-12 border-2 border-slate-300 rounded-xl focus:border-amber-400 focus:ring-4 focus:ring-amber-200/40 transition-all duration-300 bg-slate-50 focus:bg-white appearance-none cursor-pointer"
              required
            >
              <option value="">Sélectionner {getFieldDisplayName(field).toLowerCase()}...</option>
              {moroccanCities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>

            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      );
    }

    if (field === "villes") {
      return (
        <div key={field} className="space-y-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <label className="flex items-center gap-3 text-sm font-bold text-slate-800">
            <span className="text-xl">🗺️</span>
            {getFieldDisplayName(field)}
            <span className="text-red-500">*</span>
          </label>

          <div className="space-y-3">
            <div className="relative">
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value && !selectedCities.includes(e.target.value)) {
                    const newCities = [...selectedCities, e.target.value];
                    setSelectedCities(newCities);
                    setFormData((prev) => ({ ...prev, [field]: newCities }));

                    if (isCircuitModel) {
                      setSelectedHotelByCity((prev) => ({ ...prev, [e.target.value]: prev?.[e.target.value] ?? null }));
                    }
                  }
                  e.target.value = "";
                }}
                className="w-full px-4 py-3 pr-12 border-2 border-slate-300 rounded-xl focus:border-amber-400 focus:ring-4 focus:ring-amber-200/40 transition-all duration-300 bg-slate-50 focus:bg-white appearance-none cursor-pointer"
              >
                <option value="">🏙️ Ajouter une ville au circuit...</option>
                {moroccanCities
                  .filter((city) => !selectedCities.includes(city))
                  .map((city) => (
                    <option key={city} value={city}>
                      📍 {city}
                    </option>
                  ))}
              </select>

              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
              <span>💡</span>
              <span>Sélectionnez plusieurs villes pour créer un circuit complet</span>
            </div>

            {selectedCities.length > 0 ? (
              <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-xl">
                {selectedCities.map((city, index) => (
                  <span
                    key={`${city}-${index}`}
                    className="inline-flex items-center gap-1 px-3 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium border border-amber-200"
                  >
                    📍 {city}
                    <button
                      type="button"
                      onClick={() => {
                        const newCities = selectedCities.filter((_, i) => i !== index);
                        setSelectedCities(newCities);
                        setFormData((prev) => ({ ...prev, [field]: newCities }));

                        if (isCircuitModel) {
                          setSelectedHotelByCity((prev) => {
                            const next = { ...prev };
                            delete next[city];
                            return next;
                          });
                        }
                      }}
                      className="ml-1 text-amber-600 hover:text-amber-800 transition-colors hover:bg-amber-200 rounded-full w-4 h-4 flex items-center justify-center"
                      title={`Supprimer ${city}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 bg-slate-50 rounded-xl border-2 border-dashed border-slate-300">
                <div className="text-4xl mb-2">🗺️</div>
                <p className="text-slate-500 font-medium">Aucune ville sélectionnée</p>
                <p className="text-sm text-slate-400">Utilisez le menu déroulant ci-dessus pour ajouter des villes</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (field === "role") {
      const enumValues = ["user", "admin"];
      return (
        <div key={field} className="space-y-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <label className="flex items-center gap-3 text-sm font-bold text-slate-800">
            <span className="text-xl">👤</span>
            {getFieldDisplayName(field)}
          </label>

          <div className="relative">
            <select
              value={value}
              onChange={(e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }))}
              className="w-full px-4 py-3 pr-12 border-2 border-slate-300 rounded-xl focus:border-orange-400 focus:ring-4 focus:ring-orange-200/40 transition-all duration-300 bg-white appearance-none cursor-pointer"
              required
            >
              <option value="" disabled>
                Sélectionner {getFieldDisplayName(field).toLowerCase()}...
              </option>
              {enumValues.map((option) => (
                <option key={option} value={option}>
                  {option === "user" ? "Utilisateur" : option === "admin" ? "Administrateur" : option}
                </option>
              ))}
            </select>

            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      );
    }

    if (["climatisation", "disponible", "assurance_incluse", "kilometrage_illimite"].includes(field)) {
      const yesNoOptions = [
        { value: "oui", label: "Oui" },
        { value: "non", label: "Non" },
      ];
      const fieldIcons = {
        climatisation: "❄️",
        disponible: "✅",
        assurance_incluse: "🛡️",
        kilometrage_illimite: "🛣️",
      };

      return (
        <div key={field} className="space-y-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <label className="flex items-center gap-3 text-sm font-bold text-slate-800">
            <span className="text-xl">{fieldIcons[field] || "✅"}</span>
            {getFieldDisplayName(field)}
            <span className="text-red-500 ml-1">*</span>
          </label>

          <div className="relative">
            <select
              value={value}
              onChange={(e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }))}
              className="w-full px-4 py-3 pr-12 border-2 border-slate-300 rounded-xl focus:border-orange-400 focus:ring-4 focus:ring-orange-200/40 transition-all duration-300 bg-white appearance-none cursor-pointer"
              required
            >
              <option value="">Sélectionner...</option>
              {yesNoOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      );
    }

    if (field === "demande_facture" || schemaType === "boolean") {
      return (
        <div key={field} className="space-y-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <label className="flex items-center gap-3 text-sm font-bold text-slate-800">
            <span className="text-xl">✅</span>
            {getFieldDisplayName(field)}
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={value === true || value === "true"}
              onChange={(e) => setFormData((prev) => ({ ...prev, [field]: e.target.checked }))}
              className="w-5 h-5 text-amber-500 border-2 border-slate-300 rounded focus:ring-amber-400 focus:ring-2"
            />
            <span className="text-sm text-slate-700">{field === "demande_facture" ? "Demande de facture" : "Activé"}</span>
          </label>
        </div>
      );
    }

    if (isDateTimeField(field)) {
      const formatDateTimeForInput = (dateValue) => {
        if (!dateValue) return "";
        try {
          const date = new Date(dateValue);
          if (isNaN(date.getTime())) return "";
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const hours = String(date.getHours()).padStart(2, "0");
          const minutes = String(date.getMinutes()).padStart(2, "0");
          return `${year}-${month}-${day}T${hours}:${minutes}`;
        } catch {
          return "";
        }
      };

      return (
        <div key={field} className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <span className="text-lg">🕒</span>
            {getFieldDisplayName(field)}
          </label>

          <input
            type="datetime-local"
            value={formatDateTimeForInput(value)}
            onChange={(e) => {
              const dateValue = e.target.value ? new Date(e.target.value).toISOString() : "";
              setFormData((prev) => ({ ...prev, [field]: dateValue }));
            }}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:border-orange-400 focus:ring-4 focus:ring-orange-200/40 transition-all duration-300"
          />
        </div>
      );
    }

    const isTextarea =
      schema?.[field]?.type === "textarea" || ["description", "commentaire", "comment", "message"].includes(field.toLowerCase());

    return (
      <div key={field} className="space-y-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <label className="flex items-center gap-3 text-sm font-bold text-slate-800">
          <span className="text-xl">{icon}</span>
          {getFieldDisplayName(field)}
        </label>

        {isTextarea ? (
          <textarea
            value={value}
            onChange={(e) => updateField(field, e.target.value)}
            placeholder={getFieldPlaceholder(field)}
            rows={4}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:border-amber-400 focus:ring-4 focus:ring-amber-200/40 transition-all duration-300 resize-none bg-slate-50 focus:bg-white"
          />
        ) : (
          <input
            type={schemaType === "number" ? "number" : "text"}
            value={value}
            onChange={(e) => updateField(field, e.target.value)}
            placeholder={getFieldPlaceholder(field)}
            min={schemaType === "number" ? 0 : undefined}
            step={schemaType === "number" ? "any" : undefined}
            className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:border-orange-400 focus:ring-4 focus:ring-orange-200/40 transition-all duration-300"
          />
        )}

        {getFieldHint(field) && <p className="text-xs text-slate-500">{getFieldHint(field)}</p>}
      </div>
    );
  };

  const fieldsToRender = useMemo(() => {
    return Object.keys(schema || {}).filter((field) => field !== "_id" && field !== "createdAt" && field !== "updatedAt" && field !== "__v");
  }, [schema]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[70] p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden my-8 border border-slate-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-3 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{initialData ? `Modifier ${modelName}` : `Nouveau ${modelName}`}</h2>
                <p className="text-white/90 text-sm mt-1">
                  {initialData ? "Mettre à jour les informations existantes" : "Créer un nouvel élément dans le système"}
                </p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="group p-2 hover:bg-white/20 rounded-xl transition-all duration-200 text-white/80 hover:text-white hover:scale-110"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] bg-slate-50">
          <form id="admin-form" onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">{fieldsToRender.map((f) => renderField(f))}</div>

            {/* ✅ Circuit: section hôtels par ville */}
            {renderHotelsByCitySelector()}
          </form>
        </div>

        {/* Footer */}
        <div className="bg-white px-6 py-2 border-t border-slate-200 rounded-b-2xl">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all duration-200 font-medium border border-slate-300 hover:border-slate-400"
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Annuler
              </span>
            </button>

            <button
              type="submit"
              form="admin-form"
              disabled={isSubmitting}
              className={`flex-1 px-4 py-2 text-white rounded-xl transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-[1.02] ${isSubmitting
                ? "bg-slate-400 cursor-not-allowed"
                : "bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                }`}
            >
              <span className="flex items-center justify-center gap-2">
                {isSubmitting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Traitement...
                  </>
                ) : initialData ? (
                  <>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                    Modifier
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Créer
                  </>
                )}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Error Modal */}
      {errorModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[80] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300">
            <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 rounded-t-2xl">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Erreur</h3>
                  <p className="text-white/90 text-sm">Une erreur s'est produite lors de l'opération</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-slate-700 leading-relaxed">{errorModal.message}</p>
            </div>

            <div className="bg-slate-50 px-6 py-4 rounded-b-2xl">
              <button
                onClick={() => setErrorModal({ show: false, message: "" })}
                className="w-full px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
              >
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Fermer
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleAdminItemForm;
